/**
 * apiClient — Lane B REST client for the same-origin `/api/v1` surface.
 *
 * TypeScript port of the design-pack kit client
 * (NU-DATA-DASHBOARD/kits/_core/api-client.js) for issue #601 S1-3, with two
 * deliberate deletions from the source:
 *
 *   1. The localStorage-string-key `tokenStore` mode is GONE. The only
 *      accepted form is the #598 function seam: `() => string | null`
 *      (production wiring: `getAuthToken` from services/tokenStore). No API
 *      client may name a storage key.
 *   2. The `window.NuMockServer` short-circuit is GONE — no mock code in the
 *      production bundle. Tests intercept `fetch` (jest mocks / msw-style
 *      fakes) instead.
 *
 * Kept from the kit contract: JSON Content-Type negotiation, Bearer injection,
 * exponential-backoff retry on 5xx + network errors (max 2 retries),
 * AbortController via `signal`, 401 → `onUnauthorized` callback, and rejection
 * with an `ApiError` carrying `{ status, code, message, body, retried }`.
 *
 * Scope: this client serves the Lane B REST surface only. Lane A JSON-RPC
 * stays on services/rpc/RpcClient.ts + VITE_RPC_URL — do not merge them.
 */

import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  clearAuthValue,
  getAuthToken,
} from "./tokenStore";
import { redirectToLogin } from "../utils/navigation";

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

export interface ApiErrorInit {
  status: number;
  code?: string | null;
  message?: string;
  body?: unknown;
  retried?: number;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly body: unknown;
  readonly retried: number;

  constructor({ status, code, message, body, retried }: ApiErrorInit) {
    super(message || `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code ?? null;
    this.body = body ?? null;
    this.retried = retried ?? 0;
  }
}

// ---------------------------------------------------------------------------
// Options / types
// ---------------------------------------------------------------------------

/** The ONLY accepted token source: the #598 function seam. */
export type TokenSource = () => string | null;

export interface ApiClientOptions {
  baseUrl?: string;
  tokenStore?: TokenSource | null;
  onUnauthorized?: (() => void) | null;
  maxRetries?: number;
  retryDelayMs?: number;
  defaultHeaders?: Record<string, string>;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  query?: Record<
    string,
    string | number | boolean | null | undefined | Array<string | number>
  >;
  body?: unknown;
  // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
  signal?: AbortSignal;
  // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
  credentials?: RequestCredentials;
}

export type ApiClient = ReturnType<typeof createApiClient>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function resolveToken(tokenStore: TokenSource | null): string | null {
  if (typeof tokenStore !== "function") return null;
  try {
    return tokenStore() || null;
  } catch {
    return null;
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildUrl(
  baseUrl: string,
  path: string,
  query?: RequestOptions["query"],
): string {
  let url =
    (baseUrl || "").replace(/\/+$/, "") +
    "/" +
    String(path || "").replace(/^\/+/, "");
  if (query && typeof query === "object") {
    const usp = new URLSearchParams();
    for (const key of Object.keys(query)) {
      const value = query[key];
      if (value == null) continue;
      if (Array.isArray(value)) {
        value.forEach((item) => usp.append(key, String(item)));
      } else {
        usp.append(key, String(value));
      }
    }
    const qs = usp.toString();
    if (qs) url += (url.includes("?") ? "&" : "?") + qs;
  }
  return url;
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

export function createApiClient(opts: ApiClientOptions = {}) {
  const config = {
    baseUrl: opts.baseUrl || "",
    tokenStore: opts.tokenStore ?? null,
    onUnauthorized: opts.onUnauthorized ?? null,
    maxRetries: opts.maxRetries == null ? 2 : opts.maxRetries,
    retryDelayMs: opts.retryDelayMs || 400,
    defaultHeaders: opts.defaultHeaders || {},
  };

  async function request(
    method: string,
    path: string,
    options: RequestOptions = {},
  ): Promise<unknown> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      ...config.defaultHeaders,
      ...(options.headers || {}),
    };
    const token = resolveToken(config.tokenStore);
    if (token && !headers.Authorization) {
      headers.Authorization = `Bearer ${token}`;
    }

    // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
    let body: BodyInit | undefined;
    if (
      options.body != null &&
      typeof options.body === "object" &&
      !(options.body instanceof FormData) &&
      !(options.body instanceof Blob)
    ) {
      body = JSON.stringify(options.body);
      if (!headers["Content-Type"])
        headers["Content-Type"] = "application/json";
    } else {
      // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
      body = options.body as BodyInit | undefined;
    }

    const url = buildUrl(config.baseUrl, path, options.query);
    // eslint-disable-next-line no-undef -- DOM lib type; core no-undef is not TS-aware
    const init: RequestInit = {
      method,
      headers,
      body,
      signal: options.signal,
      credentials: options.credentials || "same-origin",
    };

    let attempt = 0;
    let lastErr: unknown = null;
    while (attempt <= config.maxRetries) {
      try {
        const res = await fetch(url, init);
        if (res.status === 401 && typeof config.onUnauthorized === "function") {
          config.onUnauthorized();
        }
        const contentType = res.headers.get("content-type") || "";
        let parsed: unknown = null;
        if (contentType.includes("application/json")) {
          parsed = await res.json().catch(() => null);
        } else if (res.status !== 204) {
          parsed = await res.text().catch(() => null);
        }
        if (res.ok) return parsed;
        // Retry only on 5xx
        if (res.status >= 500 && attempt < config.maxRetries) {
          await sleep(config.retryDelayMs * Math.pow(2, attempt));
          attempt += 1;
          continue;
        }
        const errBody = parsed as { code?: string; message?: string } | null;
        throw new ApiError({
          status: res.status,
          code: errBody?.code ?? null,
          message: errBody?.message || res.statusText || "Request failed",
          body: parsed,
          retried: attempt,
        });
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") throw err;
        if (err instanceof ApiError) throw err;
        // Network-level error → retry
        if (attempt < config.maxRetries) {
          lastErr = err;
          await sleep(config.retryDelayMs * Math.pow(2, attempt));
          attempt += 1;
          continue;
        }
        throw new ApiError({
          status: 0,
          code: "NETWORK",
          message: (err instanceof Error && err.message) || "Network error",
          retried: attempt,
        });
      }
    }
    throw (
      lastErr ||
      new ApiError({ status: 0, code: "UNKNOWN", message: "Request failed" })
    );
  }

  return {
    config,
    request,
    get: (path: string, options?: RequestOptions) =>
      request("GET", path, options),
    post: (path: string, options?: RequestOptions) =>
      request("POST", path, options),
    put: (path: string, options?: RequestOptions) =>
      request("PUT", path, options),
    patch: (path: string, options?: RequestOptions) =>
      request("PATCH", path, options),
    del: (path: string, options?: RequestOptions) =>
      request("DELETE", path, options),
  };
}

// ---------------------------------------------------------------------------
// Default Lane B instance
// ---------------------------------------------------------------------------

/**
 * Same-origin `/api/v1` per D-7 — data.nudata.co.za serves both the SPA and
 * the Express API behind one hostname/cert (the former data.nugui.co /
 * nu-core-01 Swarm allocation is retired; see the fleet-deployment section
 * of this repo's CLAUDE.md). `VITE_API_URL` is a BUILD-time override
 * (Docker build-arg, mirroring VITE_RPC_URL); setting it at runtime does
 * nothing to a built bundle.
 */
export const API_BASE_URL = import.meta.env.VITE_API_URL || "/api/v1";

/**
 * 401 handling mirrors the existing auth-expiry path (useAuth/RpcClient):
 * drop the browser-held auth values through the tokenStore seam, then route
 * to /login via the testable navigation seam (#583).
 */
function handleUnauthorized(): void {
  clearAuthValue(AUTH_TOKEN_KEY);
  clearAuthValue(AUTH_USER_KEY);
  redirectToLogin();
}

export const apiClient = createApiClient({
  baseUrl: API_BASE_URL,
  tokenStore: getAuthToken,
  onUnauthorized: handleUnauthorized,
});

export default apiClient;
